<i18n>
{
  "de": {
    "login": "Einloggen",
    "email": "E-Mail",
    "nickname": "Nickname",
    "password": "Passwort",
    "forgotPassword": "Hast du dein Passwort vergessen?",
    "resetTitle": "Passwort zurücksetzen",
    "enterEmailForNewPassword": "Bitte gib Deine E-Mail-Adresse ein. Wir senden Dir dann einen Link zu, damit Du ein neues Passwort setzen kannst.",
    "submit": "Bestätigen",
    "recoveryLinkSent": "Wir haben einen Wiederherstellungs-Link für Dein Passwort erstellt und ihn Dir an <b>{email}</b> zugeschickt.<br><br> Bitte prüfe auch zur Sicherheit Deinen Spam-Ordner.<br>Wenn Du in ein paar Minuten noch keine E-Mail von uns erhalten hast, wiederhole diesen Vorgang.",
    "resendRecoveryLink": "Wiederherstellungslink erneut senden",
    "signup": "Registrieren",
    "welcomeHeading": "Willkommen auf getnext!",
    "welcomeText": "Auf getnext bieten Künstler*innen und Stars exklusiven Zugang zu Videos, Musik, Making-Of-Material, persönlichen Treffen, VIP-Paketen, Vorab-Premieren, Erstzugang zu Tickets und vielem mehr.",
    "welcomeUndisturbedTitle": "Ungestört",
    "welcomeUndisturbedText": "getnext ist frei von Werbung.",
    "welcomePrivacyTitle": "Privatsphäre",
    "welcomePrivacyText": "Wir teilen und verkaufen Deine Nutzungsdaten nicht an Dritte.",
    "welcomeRegisterTitle": "Jetzt registrieren",
    "welcomeRegisterText": "Die Anmeldung ist kostenlos.",
    "artistSignupText": "Wenn Du Künstler/-in bist",
    "artistSignupAnchor": "klicke bitte hier",
    "thankYou": "Vielen Dank!",
    "registrationSent": "Du erhältst nun eine Mail von uns. Zur abschließenden Bestätigung Deiner Anmeldung bitte wir Dich, den enthaltenen Link anzuklicken.",
    "mandatory": "Pflichtfelder",
    "problemLogin": "Hast Du Schwierigkeiten, Dich einzuloggen?<br/>Bitte <a href=\"mailto:support@getnext.to?subject=Kann%20mich%20nicht%20einloggen&body=Und%20das%20ist%20passiert...\">sende uns eine E-Mail</a> und wir helfen Dir, das Problem zu lösen!",
    "policyAcceptance": "Für die Nutzung der Plattform gelten die",
    "terms": "Allgemeinen Nutzungsbedingungen",
    "cancel": "Abbrechen",
    "createAccount": "Account erstellen"
  },
  "en": {
    "login": "Log in",
    "email": "Email",
    "nickname": "Nickname",
    "password": "Password",
    "forgotPassword": "Forgot your password?",
    "resetTitle": "Reset password",
    "enterEmailForNewPassword": "Please enter your email address and we'll send you a link to setup a new password.",
    "submit": "Submit",
    "recoveryLinkSent": "We've sent a link to <b>{email}</br> to setup a new password for your account.<br><br>Please also check your SPAM-Folder.<br>In case you didn't receive that email within a couple of minutes, please try to resend it.",
    "resendRecoveryLink": "Resend Recovery Link",
    "signup": "Sign up",
    "welcomeHeading": "Welcome to getnext!",
    "welcomeText": "At getnext, artists and stars offer exclusive access to videos, music, making-of material, personal meetings, VIP packages, previews, early access to tickets and much more.",
    "welcomeUndisturbedTitle": "Just Joy",
    "welcomeUndisturbedText": "getnext is free of advertising.",
    "welcomePrivacyTitle": "Privacy",
    "welcomePrivacyText": "We do not share or sell your usage data to third parties.",
    "welcomeRegisterTitle": "Join now",
    "welcomeRegisterText": "The sign up is free.",
    "artistSignupText": "If you are an artist",
    "artistSignupAnchor": "please click here",
    "thankYou": "Thank you!",
    "registrationSent": "You will receive an email from us confirming your sign up request. Please click on the included confirmation link to complete your registration.",
    "mandatory": "Mandatory fields",
    "problemLogin": "Trouble logging in?<br/>Please <a href=\"mailto:support@getnext.to?subject=Cannot%20log%20in&body=And%20this%20is%20what%20happened...\">send us an email</a> and we will help you solve the problem!",
    "policyAcceptance": "By creating your account, you accept our",
    "terms": "terms and conditions",
    "cancel": "Cancel",
    "createAccount": "Create account"
  }
}
</i18n>

<template>
  <div id="login-pane">
    <div class="columns">
      <div
        class="column mdc-theme--light"
        :class="{
          'is-6 is-offset-3': !params.isWizard,
          'is-10 is-offset-1': params.isWizard
        }"
      >
        <div v-show="showLogin">
          <h3 class="title is-3 has-text-centered mb-0">
            {{ $t('login') }}
          </h3>
          <form
            novalidate
            method="post"
            @submit.prevent="submitCredentials"
          >
            <input-text
              id="email"
              v-model="email"
              :label="$t('email')"
              name="email"
              scope="login"
              validate="required|validEmailAddress"
            />

            <input-text
              id="password"
              v-model="password"
              type="password"
              scope="login"
              :label="$t('password')"
              name="password"
              validate="required"
              :additionalFieldClasses="['mb-2']"
            />

            <div class="password-forgotten has-text-right">
              <div v-if="params.isWizard">
                <span
                  v-if="!showRecovery && !showRegistration"
                  :disabled="errors.has('login.email')"
                  class="is-clickable is-small is-fake-anchor"
                  tabindex="0"
                  @click.prevent="activateRecovery(true)"
                >
                  {{ $t('forgotPassword') }}
                </span>
              </div>
              <nuxt-link
                v-else
                id="recovery"
                :to="localePath({ name: 'portal-password-recovery' })"
                class="is-fullwidth is-small"
              >
                {{ $t('forgotPassword') }}
              </nuxt-link>
            </div>

            <div class="control mt-50">
              <button
                id="submit-login"
                type="submit"
                :disabled="errors.any('login')"
                class="button is-primary is-outlined is-fullwidth is-medium"
              >
                {{ $t('submit') }}
              </button>
              <input
                type="submit"
                style="position: absolute; left: -9999px"
              >
            </div>
          </form>
        </div>
        <div v-show="showRecovery">
          <h3 class="title is-3 has-text-centered">
            {{ $t('resetTitle') }}
          </h3>
          <form
            v-if="!recoverySent"
            novalidate
            method="post"
            @submit.prevent="submitRecovery"
          >
            <p class="has-text-grey">
              {{ $t('enterEmailForNewPassword') }}
            </p>

            <input-text
              id="email"
              v-model="email"
              :label="$t('email')"
              name="email"
              scope="recovery"
              validate="required|validEmailAddress"
            />

            <div class="control mt-50">
              <div class="button-column-group">
                <button
                  id="submit-recovery"
                  type="submit"
                  :disabled="errors.any('recovery')"
                  class="button is-primary is-outlined is-fullwidth is-medium"
                >
                  {{ $t('submit')
                  }}
                </button>
                <button
                  class="button mt-20 is-outlined is-fullwidth is-medium is-in-column"
                  @click.prevent="activateRecovery(false)"
                >
                  {{ $t('cancel') }}
                </button>
              </div>

              <input
                type="submit"
                style="position: absolute; left: -9999px"
              >
            </div>
          </form>
          <div v-else>
            <p
              class="has-text-grey"
              v-html="$t('recoveryLinkSent', { email: email })"
            />
            <button
              class="button is-primary mt-20 is-outlined is-fullwidth is-medium"
              @click.prevent="resendRecovery"
            >
              {{ $t('resendRecoveryLink') }}
            </button>
          </div>
        </div>

        <div v-show="showRegistration">
          <h3 class="title is-3 has-text-centered">
            {{ $t('signup') }}
          </h3>
          <div
            v-if="!registrationSent"
            class="columns mt-25 mb-25"
          >
            <div class="column is-12 is-offset-0 mdc-theme--light">
              <h3 class="title is-5 is-primary-color is-bold is-italic">
                {{ $t('welcomeHeading') }}
              </h3>
              <p>{{ $t('welcomeText') }}</p>
              <dl
                id="registration-description-list"
                class="mt-10 mb-15"
              >
                <dt>{{ $t('welcomeUndisturbedTitle') }}</dt>
                <dd>{{ $t('welcomeUndisturbedText') }}</dd>
                <dt>{{ $t('welcomePricacyTitle') }}</dt>
                <dd>{{ $t('welcomePricacyText') }}</dd>
                <dt>{{ $t('welcomeRegisterTitle') }}</dt>
                <dd>{{ $t('welcomeRegisterText') }}</dd>
              </dl>
              <p>
                {{ $t('artistSignupText') }}, <a href="/#apply">
                  {{ $t('artistSignupAnchor') }}</a>.
              </p>
            </div>
          </div>
          <div class="columns">
            <div class="column is-12 is-offset-0 mdc-theme--light">
              <div v-if="registrationSent">
                <h4 class="has-text-centered">
                  {{ $t('thankYou') }}
                </h4>
                <h4 class="has-text-centered">
                  {{ $t('registrationSent') }}
                </h4>
              </div>
              <form
                v-if="!registrationSent"
                novalidate
                method="post"
                @submit.prevent="submitRegistration"
              >
                <div class="columns is-gapless is-formfields">
                  <div class="column">
                    <input-text
                      id="email"
                      v-model="email"
                      :label="$t('email')"
                      name="email"
                      scope="registration"
                      validate="required|validEmailAddress|uniqueEmailAddress"
                      class="is-medium"
                    />
                  </div>
                </div>
                <div class="columns is-gapless is-formfields">
                  <div class="column">
                    <input-text
                      id="nickname"
                      v-model="nickname"
                      name="nickname"
                      :label="$t('nickName')"
                      scope="registration"
                      autocomplete="off"
                      validate="required|uniqueNickname"
                      class="is-medium"
                    />
                  </div>
                </div>
                <div class="columns is-gapless is-formfields">
                  <div class="column">
                    <input-text
                      id="password"
                      v-model="password"
                      type="password"
                      name="password"
                      :label="$t('password')"
                      scope="registration"
                      validate="required|passwordStrength"
                      autocomplete="off"
                      class="is-medium"
                    />
                    <passwordMeter
                      id="passwordMeter"
                      :value="password"
                    />
                  </div>
                </div>
                <div class="columns is-gapless is-formfields">
                  <div class="column">
                    <div class="field">
                      {{ $t('policyAcceptance') }} <nuxt-link
                        :to="localePath({ name: 'portal-static-terms' })"
                        target="_blank"
                      >
                        {{ $t('terms') }}
                      </nuxt-link>.
                    </div>
                  </div>
                </div>
                <div class="columns is-gapless">
                  <div class="column">
                    (*) {{ $t('mandatory') }}
                  </div>
                </div>
                <div class="control mt-50">
                  <div class="button-column-group">
                    <button
                      id="submit-registration"
                      type="submit"
                      :disabled="errors.any('registration')"
                      class="button is-primary is-outlined is-fullwidth is-medium"
                    >
                      {{ $t('createAccount') }}
                    </button>
                    <button
                      class="button mt-20 is-outlined is-fullwidth is-medium is-in-column"
                      @click.prevent="activateRegistration(false)"
                    >
                      {{ $t('cancel') }}
                    </button>
                  </div>
                  <input
                    type="submit"
                    style="position: absolute; left: -9999px"
                  >
                </div>
              </form>
            </div>
          </div>
        </div>

        <div v-if="params.isWizard">
          <div>
            <button
              v-if="!showRegistration && !showRecovery"
              class="button is-secondary mt-20 is-outlined is-fullwidth is-medium"
              @click.prevent="activateRegistration(true)"
            >
              {{ $t('signup') }}
            </button>
          </div>
        </div>
      </div>
    </div>
    <div class="columns">
      <div class="column is-10 is-offset-1">
        <p
          class="has-text-centered"
          v-html="$t('problemLogin')"
        />
      </div>
    </div>
  </div>
</template>

<script>
import { mapActions, mapState } from 'vuex'
import passwordMeter from '~/components/password-meter'

export default {
  name: 'loginComponent',
  serverCacheKey: () => 'login-component',
  inject: ['$validator'],
  props: ['from', 'params'],
  components: { passwordMeter },
  data: () => ({
    email: '',
    password: '',
    nickname: '',
    showLogin: true,
    showRecovery: false,
    showRegistration: false,
    recoverySent: false,
    registrationSent: false
  }),

  mounted() {
    this.$validator.localize(this.$i18n.locale)
  },

  computed: {
    ...mapState(['artist'])
  },

  methods: {
    ...mapActions(['login', 'storeReturnRouteObject', 'storeRedirectPath', 'refreshSinglePost']),
    async submitCredentials() {
      const valid = await this.$validator.validate('login.*')
      if (valid) {
        const data = await this.apiPost('/api/private/authenticate', this.$data)
        if (data) {
          data.from = this.from

          if (this.$route.query.redirect) {
            this.storeRedirectPath(this.$route.query.redirect)
          }

          if (this.params && this.params.returnRouteObject) {
            this.storeReturnRouteObject(this.params.returnRouteObject)
          }

          const navigationHandled = await this.login(data)

          if (this.params && this.params.onPost) {
            this.$root.$emit('refreshAPI', { force: true })
            await this.refreshSinglePost({ urlAlias: this.artist.urlAlias })
            return
          }

          // Only reload if navigation wasn't fully handled by window.location methods
          if (!navigationHandled && window) {
            window.location.reload()
          }
        }
      }
    },
    activateRecovery(active) {
      if (!active) {
        this.recoverySent = false
        this.$emit('viewChanged', this.$t('login'))
      } else {
        this.$emit('viewChanged', this.$t('resetTitle'))
      }
      this.showLogin = !active
      this.showRecovery = active
      this.recoverySent = !active
    },
    activateRegistration(active) {
      if (!active) {
        this.registrationsSent = false
        this.$emit('viewChanged', this.$t('login'))
      } else {
        this.$emit('viewChanged', this.$t('signup'))
      }
      this.showRegistration = active
      this.showLogin = !active
      this.recoverySent = !active
    },
    async submitRecovery() {
      const valid = await this.$validator.validate('recovery.*')
      if (valid) this.resendRecovery()
    },
    async resendRecovery() {
      const data = await this.apiPost('/api/private/password-recovery', {
        email: this.email,
        loginRedirectRoute: this.getLoginRedirectRoute()
      })
      if (data.ok) {
        this.recoverySent = true
      }
      if (data.error) {
        this.errorMessage('Unable to request password reset for that address.')
      }
    },
    async submitRegistration() {
      const valid = await this.$validator.validate('registration.*')
      if (valid) {
        const result = await this.apiPost('/api/private/register', {
          email: this.email,
          password: this.password,
          nickname: this.nickname,
          loginRedirectRoute: this.getLoginRedirectRoute()
        })
        if (result) {
          this.registrationSent = true
        }
      }
    },
    getLoginRedirectRoute() {
      return {
        name: this.$route.name,
        params: this.$route.params,
        query: this.$route.query
      }
    }
  }
}
</script>
<style lang="scss">
@import 'assets/sass/_variables';

#login-pane {
  h3 {
    color: #000000 !important;
    font-weight: 300 !important;
  }

  button {
    line-height: 1;
    padding: 10px 15px;
  }

}

.button-column-group {
  display: flex;
  flex-direction: column;
  align-content: space-between;

  button.is-in-column {
    margin-left: 0rem !important;
  }
}

.is-fake-anchor {
  color: var(--primary-color);

  &:hover {
    text-decoration: underline;
    color: hsl(0, 0%, 21%);
  }
}
</style>
